import { useEffect, useRef } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useTheme } from "@/hooks/useTheme";

const GAP = 28;

export function GridBackground({ scrollProgress }: { scrollProgress: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const reducedMotion = useReducedMotion();
  const { theme } = useTheme();
  const scrollRef = useRef(scrollProgress);
  scrollRef.current = scrollProgress;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let time = 0;

    const isDark = document.documentElement.classList.contains("dark");
    const bgFill = isDark ? "#0a0a0a" : "#FFFFFF";
    const dotRgb = isDark ? "255, 255, 255" : "0, 0, 0";

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * window.devicePixelRatio;
      canvas.height = height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.fillStyle = bgFill;
      ctx.fillRect(0, 0, width, height);

      const scroll = scrollRef.current;
      const freqMod = 0.015 + Math.cos(time * 0.0002) * 0.005;

      for (let x = GAP; x < width; x += GAP) {
        for (let y = GAP; y < height; y += GAP) {
          const cosX = Math.cos(x * freqMod + time * 0.0004);
          const cosY = Math.cos(y * freqMod - time * 0.0003);
          const modulation = cosX * cosY;

          const baseRadius = 1.2;
          const radius = baseRadius + modulation * (1.8 + scroll * 1.5);

          const alpha = 0.06 + Math.abs(modulation) * 0.1;

          ctx.beginPath();
          ctx.arc(x, y, Math.max(0.3, radius), 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${dotRgb}, ${alpha})`;
          ctx.fill();
        }
      }

      if (!reducedMotion) {
        time += 16;
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [reducedMotion, theme]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        pointerEvents: "none",
        mixBlendMode: "var(--bg-blend-mode)" as React.CSSProperties["mixBlendMode"],
      }}
      aria-hidden="true"
    />
  );
}
